import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { patronsApi } from "@/lib/api/patrons";
import type { PatronAuditRow } from "@/components/patrons/PatronAuditCard";

export type PatronDetail = {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  emailVerified: boolean;
  membershipId: string;
  patronAccountStatus: string;
  autoRenewalOverride: boolean | null;
  patronType: { id: string; name: string } | null;
  homeBranch: { id: string; name: string; code: string } | null;
  joinedAt: string;
};

export function usePatronDetail(userId: string | undefined, canAudit: boolean) {
  const navigate = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<PatronDetail | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [audit, setAudit] = useState<PatronAuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgDual, setMsgDual] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [autoRenewSelect, setAutoRenewSelect] = useState<"default" | "on" | "off">("default");

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const d = (await patronsApi.get(userId)) as PatronDetail;
      setDetail(d);
      setFirstName(d.firstName || "");
      setLastName(d.lastName || "");
      setPhone(d.phone || "");
    } catch {
      toast.error("Could not load patron");
      navigate("/patrons");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate, toast]);

  const loadAudit = useCallback(async () => {
    if (!userId) return;
    setAuditLoading(true);
    try {
      setAudit((await patronsApi.auditLogs(userId)) as PatronAuditRow[]);
    } catch {
      setAudit([]);
    } finally {
      setAuditLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (detail && canAudit) loadAudit();
  }, [detail, canAudit, loadAudit]);

  useEffect(() => {
    if (!detail) return;
    setAutoRenewSelect(
      detail.autoRenewalOverride === null ? "default" : detail.autoRenewalOverride ? "on" : "off"
    );
  }, [detail]);

  const saveProfile = async () => {
    if (!userId) return;
    setSavingProfile(true);
    try {
      await patronsApi.updateProfile(userId, {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || null,
      });
      toast.success("Profile updated");
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveAutoRenew = async () => {
    if (!detail) return;
    const override = autoRenewSelect === "default" ? null : autoRenewSelect === "on";
    setActionLoading(true);
    try {
      await patronsApi.setAutoRenewalOverride(detail.membershipId, override);
      toast.success("Auto-renewal preference saved");
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Could not update");
    } finally {
      setActionLoading(false);
    }
  };

  const doSuspend = async () => {
    if (!userId || !window.confirm("Suspend this patron's borrowing access?")) return;
    setActionLoading(true);
    try {
      await patronsApi.suspend(userId);
      toast.success("Patron suspended");
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const doActivate = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      await patronsApi.activate(userId);
      toast.success("Patron reactivated");
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!userId || !msgSubject.trim() || !msgBody.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingMsg(true);
    try {
      await patronsApi.sendMessage(userId, {
        subject: msgSubject.trim(),
        content: msgBody.trim(),
        dualChannel: msgDual,
      });
      toast.success(msgDual ? "Message queued (in-app and email)" : "Message sent (in-app)");
      setMsgSubject("");
      setMsgBody("");
      setMsgDual(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Send failed");
    } finally {
      setSendingMsg(false);
    }
  };

  return {
    loading,
    detail,
    firstName,
    lastName,
    phone,
    setFirstName,
    setLastName,
    setPhone,
    savingProfile,
    actionLoading,
    audit,
    auditLoading,
    msgSubject,
    msgBody,
    msgDual,
    sendingMsg,
    setMsgSubject,
    setMsgBody,
    setMsgDual,
    autoRenewSelect,
    setAutoRenewSelect,
    saveProfile,
    saveAutoRenew,
    doSuspend,
    doActivate,
    sendMessage,
  };
}
